import { useCallback, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { motion } from 'motion/react';
import { Box } from '@comeon/ui';
import Icon, { ICON_SHARE_LINE } from '~/components/icon/index.js';
import useWebShare from '../../../../hooks/use-web-share';
import useTranslations from '../../../translation/use-translations';
import { showNotification } from '../../../notifications/ducks/notifications.js';
import { WE_SPIN_SHARE_TEXT_KEYS } from '../constants/we-spin-text-keys.js';
import { ROUTE_WE_SPIN_GAME_ROOM_EVENT_LIST } from '../constants/we-spin-route-constants.js';
import useContextualPathTransformer from '../../../../hooks/use-contextual-path-transformer.js';

const ENDPOINT = '/we-spin-api/shortenUrl';

const BoxWithBackgroundColor = styled(Box)`
	background-color: var(--button-wrapper-bg);
	position: relative;
	min-height: 80px;
	> button > svg {
		padding-right: 3px;
		margin-right: 8px;
		vertical-align: middle;
		display: inline-block;
	}
`;

/**
 * This component renders a button that will allow you to share a link with your friends. The idea here is to make it easier
 * for players in WeSpin to get their friends to join, or check out the session/events.
 *
 * We try to use the native Web Share API if it is possible, but due to lacking browser support we have to provide a fallback
 * for users who doesn't have the support. In that case, we copy the URL to the clipboard and notify the user.
 */

const WeSpinShare = ({ sessionRef }) => {
	const dispatch = useDispatch();
	const transformPath = useContextualPathTransformer();
	const [isShortUrlLoading, setIsShortUrlLoading] = useState(false);
	const [rebrandlyUrl, setRebrandlyUrl] = useState(null);
	const { translations: tr, isLoadingTranslations } = useTranslations(WE_SPIN_SHARE_TEXT_KEYS);

	useEffect(() => {
		// reset the url in state when clicking another session
		setRebrandlyUrl(null);
	}, [sessionRef]);

	const { origin } = window.location;
	const pathname = transformPath(`${ROUTE_WE_SPIN_GAME_ROOM_EVENT_LIST}/${sessionRef}`);
	const longUrl = `${origin}${pathname}`;

	const handleShareSuccess = () => {
		dispatch(
			showNotification({
				message: tr.WE_SPIN_SHARE_SUCCESS,
				color: 'green',
				autoHide: true,
				openInSidebar: true
			})
		);
	};

	const copyUrlToClipboard = useCallback(
		shortUrl => {
			navigator.clipboard
				.writeText(shortUrl)
				.then(() => {
					dispatch(
						showNotification({
							message: tr.WE_SPIN_SHARE_URL_COPIED_TO_CLIPBOARD,
							color: 'green',
							autoHide: true,
							openInSidebar: true
						})
					);
				})
				.catch(err => console.warn(err)); // eslint-disable-line
		},
		[dispatch, tr.WE_SPIN_SHARE_URL_COPIED_TO_CLIPBOARD]
	);

	const { isSupported, share } = useWebShare(handleShareSuccess);

	const handleOnClickShare = useCallback(() => {
		if (isSupported) {
			share({
				title: tr.WE_SPIN_SHARE_TITLE,
				text: tr.WE_SPIN_SHARE_MESSAGE,
				url: rebrandlyUrl
			});
		} else {
			copyUrlToClipboard(rebrandlyUrl);
		}
	}, [share, isSupported, rebrandlyUrl, copyUrlToClipboard, tr.WE_SPIN_SHARE_TITLE, tr.WE_SPIN_SHARE_MESSAGE]);

	const fetchShortUrl = useCallback(async () => {
		try {
			const response = await fetch(ENDPOINT, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({ url: longUrl })
			}).then(data => data.json());

			return response;
		} catch (e) {
			dispatch(
				showNotification({
					message: tr.WE_SPIN_SHARE_FAILURE,
					color: 'red',
					autoHide: true,
					openInSidebar: true
				})
			);
		}
	}, [dispatch, longUrl, tr.WE_SPIN_SHARE_FAILURE]);

	const handleOnClickInvite = useCallback(() => {
		setIsShortUrlLoading(true);
		fetchShortUrl().then(({ shortUrl }) => {
			const urlWithHttps = `https://${shortUrl}`;
			setRebrandlyUrl(urlWithHttps);
			setIsShortUrlLoading(false);
		});
	}, [fetchShortUrl]);

	if (isLoadingTranslations) return null;
	return (
		<BoxWithBackgroundColor mt="8" p="5" className="wespin-social-share">
			{!isShortUrlLoading && !rebrandlyUrl && (
				<button className="btn btn--secondary" onClick={handleOnClickInvite}>
					{tr.WE_SPIN_SHARE_BUTTON_TEXT}
				</button>
			)}
			{isShortUrlLoading && <div className="spinner" />}
			{!isShortUrlLoading && rebrandlyUrl && (
				<motion.button
					className="btn btn--secondary"
					onClick={handleOnClickShare}
					initial={{ opacity: 0, scale: 0.9 }}
					animate={{ opacity: 1, scale: 1 }}
					transition={{
						scale: {
							type: 'spring',
							stiffness: 300,
							damping: 15,
							velocity: 3
						}
					}}
				>
					<Icon type={ICON_SHARE_LINE} />
					{tr.WE_SPIN_SHARE_BUTTON_SHARE_LINK}
				</motion.button>
			)}
		</BoxWithBackgroundColor>
	);
};

export default WeSpinShare;
